import type { WaysteClient } from '@alliance-disposal/client';
import type { Hauler } from '@alliance-disposal/transport-types';
import type { AppDataAdapter } from '@wayste/sour-components';
import { WaystePaymentMethods } from './shared-types';

export type WaysteS3Bindings = {
    uploadOrderImage: (
        file: File,
        orderId: string,
        account: Hauler.HaulerTransport,
    ) => Promise<{ bucket: string; key: string; region: string }>;
    deleteS3FileFromFolder: (key: string) => Promise<boolean>;
};

/**
 * Wayste's implementation of the shared roll-off data contract. Binds the package's
 * app-agnostic hooks and components to the `wayste` client namespaces.
 *
 * Upload and delete come from `S3Context`, which builds the `<haulerId>/<orderId>/...` key and
 * tracks upload state at the React tree level. The hauler is read from the client at call time
 * so the shared components never need it. Fetch goes straight to the client so the stored
 * bucket can be passed through.
 */
export const buildWaysteAppDataAdapter = (client: WaysteClient, s3: WaysteS3Bindings): AppDataAdapter => {
    const customer = client.customer().wayste;
    const pricing = client.pricing();
    const equipment = client.equipment().wayste;
    const internalTicket = client.internalTicket().wayste;
    const receivable = client.invoice().wayste.receivable;
    const image = client.upload().wayste.image;

    return {
        customer: {
            fetch: customer.fetch,
            query: customer.query,
            create: customer.create,
            update: customer.update,
            addServiceLocation: customer.addServiceLocation,
            removeServiceLocation: customer.removeServiceLocation,
        },
        contacts: {
            create: customer.contact.create,
            update: customer.contact.update,
            delete: customer.contact.delete,
        },
        pricing: {
            zones: { fetch: pricing.wayste.pricing.fetch, query: pricing.wayste.pricing.query },
            discounts: { query: pricing.wayste.discount.query },
            salesTax: { query: pricing.salesTax.query },
        },
        equipment: {
            containerType: {
                fetch: equipment.containerType.fetch,
                query: equipment.containerType.query,
            },
            containerAssignment: { create: equipment.containerAssignment.create },
        },
        disposalSites: { query: client.disposalSite().wayste.query },
        capabilities: {
            rollOffDetails: {
                changeStatus: true,
                editOrder: true,
                relocateTask: true,
                duplicateOrder: true,
                driverTasks: true,
                disposalSites: true,
            },
        },
        internalTickets: {
            query: internalTicket.query,
            create: internalTicket.create,
            delete: internalTicket.delete,
            tag: { query: internalTicket.tag.query },
        },
        receivables: { query: receivable.query, update: receivable.update },
        supportedPaymentMethods: WaystePaymentMethods,
        s3: {
            uploadOrderImage: (file, orderId) => {
                const hauler = client.hauler().wayste.currentHauler;
                if (!hauler) return Promise.reject(new Error('Cannot upload an order image without a current hauler.'));
                return s3.uploadOrderImage(file, orderId, hauler);
            },
            deleteS3FileFromFolder: s3.deleteS3FileFromFolder,
            fetchImage: async (key, bucket) => ({ status: 'success', url: await image.fetch(key, bucket) }),
        },
    };
};
